import React from 'react';
import styles from '../../pages/DiagramPage/DiagramEditor.module.css';
import { ShapeType } from '../../types/diagram.types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSquare,
  faCircle,
  faDiamond,
  faPlay,
  faStop,
  faExpand,
  faPlus,
  faMinus,
  faTrash,
  faFolderOpen,
  faSave,
  faFont,
  faMinus as faLine,
  faLightbulb,
  faLock,
  faLockOpen,
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../context/LanguageContext';

interface ToolbarProps {
  onAddShape: (type: ShapeType) => void;
  onAddText: () => void;
  onAddLine: () => void;

  onClear: () => void;
  onExport: () => void;
  onImport: () => void;

  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  
  isEditMode: boolean;
  onToggleEditMode: () => void;
}

export const Toolbar: React.FC<ToolbarProps> = ({
  onAddShape,
  onAddText,
  onAddLine,

  onClear,
  onExport,
  onImport,

  zoom,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  
  isEditMode,
  onToggleEditMode,
}) => {
  const { t } = useLanguage();

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarSection}>
        <button
          className={`${styles.toolbarButton} ${isEditMode ? styles.toolbarButtonPrimary : styles.toolbarButtonSecondary}`}
          onClick={onToggleEditMode}
          title={isEditMode ? t('viewMode') : t('editMode')}
        >
          <FontAwesomeIcon icon={isEditMode ? faLockOpen : faLock} />
          {isEditMode ? ' ' + t('editing') : ' ' + t('viewing')}
        </button>
      </div>

      <div className={styles.toolbarDivider} />

      <div className={styles.toolbarSection}>
        <span className={styles.toolbarLabel}>{t('shapes')}</span>

        <button
          className={styles.toolbarButton}
          onClick={() => onAddShape('rectangle')}
          title={t('rectangle')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faSquare} />
        </button>

        <button
          className={styles.toolbarButton}
          onClick={() => onAddShape('circle')}
          title={t('circle')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faCircle} />
        </button>

        <button
          className={styles.toolbarButton}
          onClick={() => onAddShape('diamond')}
          title={t('diamond')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faDiamond} />
        </button>

        <button
          className={styles.toolbarButton}
          onClick={() => onAddShape('triangle')}
          title={t('triangle')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faPlay} />
        </button>

        <button
          className={styles.toolbarButton}
          onClick={() => onAddShape('hexagon')}
          title={t('hexagon')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faStop} />
        </button>

        <button
          className={styles.toolbarButton}
          onClick={() => onAddShape('parallelogram')}
          title={t('parallelogram')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faExpand} />
        </button>
      </div>

      <div className={styles.toolbarDivider} />

      <div className={styles.toolbarSection}>
        <span className={styles.toolbarLabel}>{t('elements')}</span>

        <button
          className={styles.toolbarButton}
          onClick={onAddText}
          title={t('text')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faFont} />
        </button>

        <button
          className={styles.toolbarButton}
          onClick={onAddLine}
          title={t('line')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faLine} />
        </button>
      </div>

      <div className={styles.toolbarDivider} />

      <div className={styles.toolbarSection}>
        <span className={styles.toolbarLabel}>{t('scale')}</span>

        <button
          className={styles.toolbarButton}
          onClick={onZoomOut}
          title={t('zoomOut')}
        >
          <FontAwesomeIcon icon={faMinus} />
        </button>

        <span className={styles.zoomLabel}>
          {Math.round(zoom * 100)}%
        </span>

        <button
          className={styles.toolbarButton}
          onClick={onZoomIn}
          title={t('zoomIn')}
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>

        <button
          className={styles.toolbarButton}
          onClick={onZoomReset}
          title={t('resetZoom')}
        >
          100%
        </button>
      </div>

      <div className={styles.toolbarDivider} />

      <div className={styles.toolbarSection}>
        <button
          className={`${styles.toolbarButton} ${styles.toolbarButtonPrimary}`}
          onClick={onImport}
          title={t('open')}
        >
          <FontAwesomeIcon icon={faFolderOpen} />
        </button>

        <button
          className={`${styles.toolbarButton} ${styles.toolbarButtonPrimary}`}
          onClick={onExport}
          title={t('save')}
        >
          <FontAwesomeIcon icon={faSave} />
        </button>

        <button
          className={`${styles.toolbarButton} ${styles.toolbarButtonDanger}`}
          onClick={onClear}
          title={t('clear')}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>

      <div className={styles.toolbarHint}>
        <FontAwesomeIcon
          icon={faLightbulb}
        />
        <strong>{t('hints')}:</strong>{' '}
        {isEditMode ? (
          <>
            {t('shiftClickConnection')} ·
            {t('doubleClickText')} ·
            {t('ctrlPanCanvas')}
          </>
        ) : (
          t('viewModeHint')
        )}
      </div>
    </div>
  );
};